/**
 * US-E1-001 — Register by email + password
 * US-E1-002 — OAuth (Google + Яндекс)
 * 152-ФЗ — ПДн consent checkbox required
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { authApi, type RegisterRequest } from '@/api/auth'
import { ApiError } from '@/api/client'
import { Icon } from '@/components/icons/Icon'
import { TextInput } from '@/components/auth/TextInput'
import { OAuthButtons } from '@/components/auth/OAuthButtons'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { ResendVerificationButton } from '@/components/auth/ResendVerificationButton'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { Button } from '@/components/ui/Button'
import { PasswordStrengthMeter } from '@/components/ui/PasswordStrengthMeter'
import { EmailRequirementsList } from '@/components/ui/EmailRequirementsList'
import { validateEmail, validatePassword } from '@/lib/validation'

export function Register() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [consentPdn, setConsentPdn] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string; consent?: string }>({})
  const [success, setSuccess] = useState(false)

  const m = useMutation({
    mutationFn: (body: RegisterRequest) => authApi.register(body),
    onSuccess: () => setSuccess(true),
    onError: (e: ApiError) => {
      if (e.code === 'email_exists') setErrors({ email: t('auth.errors.email_exists', 'Email уже зарегистрирован') })
      else if (e.code === 'invalid_email') setErrors({ email: t('auth.errors.invalid_email', 'Введи корректный email') })
      else if (e.code === 'weak_password') setErrors({ password: t('auth.errors.weak_password', 'Минимум 10 символов, буквы + цифры') })
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const errs: typeof errors = {}
    const emailErr = validateEmail(email)
    if (emailErr) errs.email = emailErr
    const pwd = validatePassword(password)
    if (pwd.error) errs.password = pwd.error
    if (!consentPdn) errs.consent = t('auth.errors.consent_required', 'Согласие на обработку ПДн обязательно')
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    m.mutate({ email: email.toLowerCase().trim(), password, consent_pdn: consentPdn })
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-status-success-bg mx-auto">
            <Icon name="check" size={32} className="text-status-success-text" />
          </div>
          <h1 className="text-3xl font-bold font-display text-text-primary text-center">
            {t('auth.register.success_title', 'Аккаунт создан!')}
          </h1>
          <p className="text-text-secondary text-center">
            {t('auth.register.success_body', 'Мы отправили письмо подтверждения на ')} <strong>{email}</strong>.
          </p>
          <div className="text-center">
            <ResendVerificationButton email={email} />
          </div>
          <a href="/auth/login" className="block text-center text-text-link font-semibold">
            {t('auth.login_here', 'Войти')}
          </a>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <a href="/" className="hidden lg:flex w-fit items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium mb-8">
        <Icon name="arrow-left" size={16} />
        {t('common.back', 'Назад')}
      </a>

      <h1 className="text-3xl font-bold font-display text-text-primary mb-2 text-center lg:text-left">
        {t('auth.register.title', 'Стань акционером 🚀')}
      </h1>
      <p className="text-text-secondary text-sm mb-10 text-center lg:text-left">
        {t('auth.register.sub', 'Найми AI CEO. 14 дней бесплатно, без карты.')}
      </p>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="reg-email" className="block text-sm font-semibold text-text-primary mb-2">Email</label>
          <TextInput
            id="reg-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(v) => { setEmail(v); if (errors.email) setErrors((e) => ({ ...e, email: undefined })) }}
            placeholder="alex@company.ru"
            leftSlot={<Icon name="mail" size={20} />}
            state={errors.email ? 'error' : email && !validateEmail(email) ? 'success' : email && validateEmail(email) ? 'error' : 'default'}
            aria-describedby="reg-email-checks"
          />
          <div id="reg-email-checks" className="mt-2">
            <EmailRequirementsList value={email} />
          </div>
        </div>
        <div>
          <label htmlFor="reg-password" className="block text-sm font-semibold text-text-primary mb-2">
            {t('auth.password', 'Пароль')}
          </label>
          <PasswordInput
            id="reg-password"
            name="password"
            value={password}
            onChange={(v) => { setPassword(v); if (errors.password) setErrors((e) => ({ ...e, password: undefined })) }}
            autoComplete="new-password"
            required
            placeholder={t('auth.password_helper', 'Минимум 10 символов')}
            leftSlot={<Icon name="lock" size={20} />}
            state={errors.password ? 'error' : password && !validatePassword(password).error ? 'success' : password && validatePassword(password).error ? 'error' : 'default'}
            aria-describedby="reg-password-meter"
          />
          <div id="reg-password-meter" className="mt-2">
            <PasswordStrengthMeter value={password} />
          </div>
        </div>

        <label htmlFor="reg-consent-pdn" className="flex items-start gap-2.5 cursor-pointer">
          <input
            id="reg-consent-pdn"
            type="checkbox"
            checked={consentPdn}
            onChange={(e) => setConsentPdn(e.target.checked)}
            aria-invalid={!!errors.consent || undefined}
            aria-describedby={errors.consent ? 'reg-consent-pdn-error' : undefined}
            /* WCAG 2.5.5 Target Size AA — 24×24 минимум */
            className="mt-0.5 w-6 h-6 accent-accent-brand-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-border-focus/30 rounded"
          />
          <span className="text-sm text-text-secondary leading-relaxed">
            {t('auth.consent_pdn', 'Я согласен с')} <a href="/legal/privacy" target="_blank" rel="noopener" className="text-text-link font-semibold">Политикой конфиденциальности</a> {t('auth.and', 'и')} <a href="/legal/terms" target="_blank" rel="noopener" className="text-text-link font-semibold">Политикой использования</a>.
          </span>
        </label>
        {errors.consent && <p id="reg-consent-pdn-error" role="alert" className="text-sm text-text-danger">{errors.consent}</p>}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={m.isPending}>
          {m.isPending ? t('common.loading', 'Создаём…') : t('auth.register.cta', 'Создать аккаунт')}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-12 text-text-muted text-xs font-medium uppercase tracking-wider">
        <span className="flex-1 h-px bg-border"/>
        {t('auth.or_oauth_register', 'или один клик через')}
        <span className="flex-1 h-px bg-border"/>
      </div>

      <OAuthButtons caption={null} />

      <p className="text-center text-sm text-text-secondary mt-8">
        {t('auth.have_account', 'Уже есть аккаунт?')}{' '}
        <a href="/auth/login" className="inline-flex items-center min-h-[24px] px-1 text-text-link font-semibold">{t('auth.login_here', 'Войти')}</a>
      </p>
    </AuthLayout>
  )
}

